import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { identity, ApiError } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Anchor, Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Checkbox } from '@repletoryreach/sdk-ui/inputs';
import { notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../i18n/I18nProvider';
import { websitePage } from '../../lib/website';
import { formatDate } from '../../lib/format';

export const AGREEMENTS_KEY = ['organisation', 'agreements'] as const;

/**
 * Accepting the data processing agreement (owner's decision, 2 October 2026).
 * Used by the onboarding step and by the gate over the console, so the wording
 * and the record are the same wherever it is asked. Who accepted, when, from
 * where and which version is recorded by identity.
 */
export function AgreementForm({ agreement, onAccepted }: { agreement: identity.AgreementStatus; onAccepted?: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const { user, isImpersonating } = useAuth();
  const [ticked, setTicked] = useState(false);
  // Loose: identity leaves an empty field out of the answer rather than sending null.
  const updated = agreement.accepted_version != null && !agreement.accepted;

  const accept = useMutation({
    mutationFn: () => identity.acceptAgreement(agreement.kind, agreement.current_version),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: AGREEMENTS_KEY });
      onAccepted?.();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'AGREEMENT_VERSION_CHANGED') {
        void qc.invalidateQueries({ queryKey: AGREEMENTS_KEY });
        setTicked(false);
      }
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('agreements.failed'));
    }
  });

  return (
    <Stack gap="md">
      <Text role="bodySmall">{t('agreements.dpa.why')}</Text>
      {updated && (
        <Alert color="blue" variant="light">
          <Stack gap={4}>
            <Text fz="sm">{t('agreements.dpa.updated', { date: formatDate(agreement.published_at) })}</Text>
            {agreement.summary && <Text fz="sm">{agreement.summary}</Text>}
          </Stack>
        </Alert>
      )}
      <Anchor href={websitePage(agreement.path)} target="_blank" rel="noopener noreferrer">
        {t('agreements.dpa.read', { version: agreement.current_version })}
      </Anchor>
      {isImpersonating ? (
        <Alert color="yellow" variant="light">{t('agreements.impersonating')}</Alert>
      ) : user?.role === 'agent' ? (
        // Binding the organisation is for an owner or admin (roles, 2 October 2026).
        <Alert color="yellow" variant="light">{t('agreements.agentWaits')}</Alert>
      ) : (
        <>
          <Checkbox
            checked={ticked}
            onChange={(checked) => setTicked(checked)}
            label={t('agreements.dpa.confirm', { org: user?.org?.name ?? '' })}
          />
          <Group justify="flex-end">
            <Button disabled={!ticked} loading={accept.isPending} onClick={() => accept.mutate()}>
              {t('agreements.dpa.accept')}
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );
}
